/**
 * Game-related types shared between frontend and backend
 */

import type { PlayerSkinPreferences } from './room';

export type GameMode = 'free' | 'paid' | 'private';
export type GameStatus = 'waiting' | 'in_progress' | 'completed' | 'cancelled';
export type PlayerColor = 'red' | 'blue' | 'green' | 'yellow';
export type PlayerStatus = 'active' | 'timeout' | 'disconnected' | 'excluded';

/**
 * Wire-safe player representation sent to clients.
 * MUST NOT contain any bot-related fields (isBot, botPoolId, tier).
 * This is the authoritative shape for all client-bound player data.
 */
export interface WirePlayer {
  /** Player identifier (odooId / userId) */
  odooId: string;
  /** Display name */
  pseudo: string;
  /** ISO 3166-1 alpha-2 country code, optional */
  country?: string;
  /** Board color assigned to this player */
  color: PlayerColor;
  /** Avatar asset key, optional */
  avatarKey?: string;
  /** Per-component skin preferences */
  skinPreferences?: PlayerSkinPreferences;
}

export interface Player {
  id: string;
  userId: string;
  username: string;
  color: PlayerColor;
  position: number; // 1-4
  status: PlayerStatus;
  isBot: boolean;
  consecutiveTimeouts: number;
  pawns: Pawn[];
  /**
   * Number of self-initiated turns the player completed in this game.
   * Increments once per completed pawn action (release or move) on a turn the
   * player rolled and acted themselves; never incremented by the automove path.
   * Bonus-roll (6-chain): each self-played action in the chain is counted
   * independently (e.g. 2 self-played moves on a 6-chain → activeTurns += 2).
   * Used by Story 01-02 to derive `validForDaily` and `activeTurnsPlayed`.
   */
  activeTurns: number;
}

/**
 * Per-match daily-rewards signal for a single human player (Story 01-02).
 *
 * Reported by LUDOKA to SOKA on match completion. LUDOKA performs no daily
 * counting itself — it only states, per completed match, whether the player
 * validly played and how many active turns they made. Bots are never emitted.
 */
export interface DailyMatchPlayerSignal {
  /** SOKA user id (the LUDOKA playerId is the odooId). */
  odooId: string;
  /** Whether this player counts toward the day per the §5 validity rule. */
  validForDaily: boolean;
  /** Self-initiated turns played (mirrors `activeTurns`). */
  activeTurnsPlayed: number;
}

export interface Pawn {
  id: string;
  playerId: string;
  position: number; // 0-57 (0 = home, 1-52 = board, 53-57 = finish zone)
  isHome: boolean;
  isFinished: boolean;
  color: PlayerColor;
}

export interface GameState {
  gameId: string;
  mode: GameMode;
  status: GameStatus;
  players: Player[];
  currentTurn: string; // playerId
  diceValue: number | null;
  turnStartedAt: string | null;
  turnTimeoutSeconds: number;
  entryFee?: number;
  prizePool?: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface GameResult {
  gameId: string;
  mode: GameMode;
  rankings: PlayerRanking[];
  prizeDistribution?: Record<string, number>; // playerId -> points earned
  completedAt: string;
}

export interface PlayerRanking {
  position: number; // 1-4
  playerId: string;
  userId: string;
  username: string;
  prize?: number;
}

export interface Lobby {
  lobbyId: string;
  mode: GameMode;
  hostId: string;
  players: LobbyPlayer[];
  maxPlayers: number;
  entryFee?: number;
  isPrivate: boolean;
  inviteCode?: string;
  status: 'waiting' | 'ready' | 'started';
  createdAt: string;
}

export interface LobbyPlayer {
  userId: string;
  username: string;
  avatarUrl?: string;
  isReady: boolean;
  isHost: boolean;
}

/**
 * Entry in the player order array for turn tracking
 * Sorted by joinedAt timestamp to determine turn order
 */
export interface PlayerOrderEntry {
  playerId: string;
  color: PlayerColor;
  joinedAt: number;
  pseudo?: string;
  skinPreferences?: PlayerSkinPreferences;
  /**
   * Whether this seat is occupied by a bot (free games only).
   * Backend-internal: always false or absent for human players.
   * Never sent to clients — the field is present on `PlayerOrderEntry`
   * (a backend Redis structure) but not on `WirePlayer` (the client-safe shape).
   */
  isBot?: boolean;
  /**
   * Pool identifier for bot seats (bots only).
   * Used by game.service to invoke bot.service.playTurn after turn advance.
   */
  botPoolId?: string;
  /**
   * Why this bot was placed in the game (bots only).
   * Backend-internal: 'fill-lone-human' | 'idle-lobby-creation'.
   * Persisted from spawn time via BotPresenceEntry → startGame → game state.
   */
  botSpawnReason?: string;
}

/**
 * Game state stored in Redis for real-time game management
 * Redis key pattern: game:${gameId}:state
 */
export interface GameStateRedis {
  gameId: string;
  currentTurnPlayerId: string;
  currentTurnColor: PlayerColor;
  lastDiceValue: number | null;
  lastDiceRollerId: string | null;
  /**
   * Identifies the current **turn instance**. Increments on every turn
   * transition — including a bonus turn granted to the same player by a rolled
   * 6, a capture, or reaching XFINAL — so an action aimed at a superseded turn
   * can be rejected even though `currentTurnPlayerId` has not changed.
   *
   * Distinct from `turnNumber`, which counts logged turns and must not be
   * overloaded. Monotonic: never decrements, never resets for a game's lifetime.
   *
   * Backward compatibility: states persisted before this field existed read as
   * `0` via `normalizeGameState()` at the `getGameState` boundary.
   */
  turnEpoch: number;
  /**
   * Whether a dice roll has already been accepted for the current `turnEpoch`.
   * Reset to `false` on every epoch increment. Becomes authoritative for roll
   * idempotency in Story 02-01.
   */
  hasRolledThisTurn: boolean;
  /**
   * Client-supplied idempotency key of the roll accepted for the current
   * `turnEpoch`, or `null` when no roll has been accepted. Reset on every epoch
   * increment.
   */
  rollId: string | null;
  playerOrder: PlayerOrderEntry[];
  /**
   * Pawn positions map storing 4 pawn positions per player
   * Key: playerId
   * Value: Array of 4 pawn positions [pawn0, pawn1, pawn2, pawn3]
   *
   * Position values:
   * - -1: Pawn in base (not released)
   * - 0-57: Pawn on board path
   * - 58-63: Pawn in finish zone
   *
   * Start positions by color:
   * - Green: 0
   * - Yellow: 13
   * - Blue: 26
   * - Red: 39
   */
  pawnPositions: Record<string, number[]>;
  /**
   * Consecutive 6s tracking per player for forfeit rule
   * Key: playerId
   * Value: Count of consecutive 6s rolled (0-3)
   *
   * Server-side tracking for security:
   * - Incremented when player rolls a 6
   * - Reset to 0 when turn ends or non-6 is rolled
   * - Third consecutive 6: Turn is forfeited, no pawn release allowed
   */
  consecutiveSixes: Record<string, number>;
  /**
   * Player IDs who have completed the game (all 4 pawns at XFINAL)
   * Stored in completion order: finishedPlayers[0] = 1st place, finishedPlayers[1] = 2nd place
   * Game ends when finishedPlayers.length === 2 (2nd place determined)
   *
   * Optional for backward compatibility with existing games
   */
  finishedPlayers?: string[];
  /**
   * Game mode for prize calculation context
   * Optional for backward compatibility with existing games
   */
  gameMode?: GameMode;
  /**
   * Timestamp when the current turn started (Unix ms)
   * Used for turn timeout calculation and state recovery after server restart
   */
  turnStartedAt?: number;
  /**
   * Turn timeout duration in seconds
   * Used by frontend for countdown display
   * Reflects the server's GAME_CONFIG.TURN_TIMEOUT_SECONDS value
   */
  turnTimeoutSeconds?: number;
  /**
   * Player IDs who are in automove mode
   * Players enter automove mode after timing out on their turn
   * Players exit automove mode by clicking "Return to Manual" button
   * When a player in automove mode's turn comes, actions are taken immediately
   */
  automovePlayers?: string[];
  /**
   * Player IDs who voluntarily left a paid game
   * These players cannot rejoin the game (active-game mapping is cleared)
   * Only populated for paid games; free games allow rejoin
   */
  leftPlayers?: string[];
  /**
   * Player IDs who voluntarily abandoned a PAID game (Story 01-02).
   * Unlike `leftPlayers`, an abandoner is NOT removed from the game: their seat
   * is routed through the automove / 3-strike chain (silent grace), so the match
   * only ends via normal completion or the all-automove timeout. This prevents a
   * colluding pair from instantly ending a paid match to extract the prize.
   * Optional for back-compat with in-flight games (treat missing as []).
   */
  voluntaryLeavers?: string[];
  /**
   * Timestamps when each player entered automove mode (Unix ms)
   * Key: playerId, Value: timestamp when they entered automove
   * Used to detect zombie games (all players in automove for extended periods)
   */
  automoveEnteredAt?: Record<string, number>;
  /**
   * Game status for lifecycle tracking
   * - 'in_progress': Game is active
   * - 'completed': Game has ended (2 players finished)
   *
   * Optional for backward compatibility with existing games
   */
  status?: 'in_progress' | 'completed';
  /**
   * Timestamp (Unix ms) when the completion was persisted and enqueued to the
   * SOKA outbox, set by `triggerGameOver` BEFORE any destructive cleanup so the
   * completion is durable even if cleanup or the SOKA PATCH later throws.
   * (Story 01-03 — durable completion reorder.)
   */
  completionEnqueuedAt?: number;
  /**
   * Timestamp (Unix ms) when the completion was confirmed synced to SOKA (2xx).
   * A `completed` state WITHOUT `syncedAt` is preserved by the Redis cleanup
   * worker so reconciliation/replay can re-drive it; a synced state is eligible
   * for normal cleanup. (Story 01-03.)
   */
  syncedAt?: number;
  /**
   * Timestamp when game started (Unix ms)
   * Used for duration calculation in match completion
   */
  startedAt?: number;
  /**
   * Whether this game originated from matchmaking (vs private room)
   * Used to determine if free game winners receive SOKA points rewards
   * Only free matchmaking games award points (20 for 1st, 5 for 2nd)
   */
  isMatchmaking?: boolean;
  /**
   * Whether the all-automove warning event has already been sent
   * Prevents publishing duplicate warning events on every automove turn
   * Reset when a player exits automove (breaking the all-automove condition)
   */
  allAutomoveWarningSent?: boolean;
  /**
   * Monotonic turn counter incremented after each pawn action (release / move / no-move).
   * Starts at 0 when the game is initialised; used to number TurnLogEntry records.
   * SERVER-ONLY: never sent to frontend clients.
   */
  turnNumber?: number;
  /**
   * Sliding-window per-turn action log (server-only).
   * Capped to the last `OPPONENT_PROFILE_HISTORY_WINDOW` entries (default 50).
   * Used by the opponent-profiling pipeline (Epic 10) to compute behavioural counters.
   * SERVER-ONLY: never sent to frontend clients.
   */
  turnLog?: TurnLogEntry[];
  /**
   * Per-bot bookkeeping: the next `turnNumber` to observe from the turn-log.
   * Set on each `observe()` call so subsequent calls skip already-counted turns.
   * Key: botPoolId, Value: turnNumber.  SERVER-ONLY.
   */
  lastBotTurnNumber?: Record<string, number>;
  /**
   * Per-player count of self-initiated completed turns.
   * Key: playerId, Value: count.
   *
   * Incremented in `movePawn` and `releasePawn` when `isAutomove = false`.
   * Never incremented by the automove path (turn-timeout auto-roll + auto-move,
   * 3-strike auto-play, or disconnected-seat automove).
   *
   * Bonus-roll (6-chain) counting rule: each completed pawn action on a
   * self-initiated turn is counted independently. A 6-chain of 2 self-played
   * moves increments activeTurns by 2.
   *
   * Consumed by Story 01-02 to derive `activeTurnsPlayed` and `validForDaily`
   * on match completion.
   *
   * Optional for backward compatibility with in-flight games (treat missing as {}).
   * SERVER-ONLY: never sent to frontend clients.
   */
  activeTurns?: Record<string, number>;
  /**
   * ISO 8601 timestamp of the last state-mutating action (roll / move / release /
   * automove / turn-advance). Initialised at game start; updated on every
   * `saveGameState` call so no extra Redis round trip is needed.
   *
   * Read-only paths (`getGameState`) do NOT bump this field.
   *
   * Used by the stuck-game watchdog (Epic 02 — Story 02-03) to detect frozen
   * games: a game whose `lastActivityAt` is older than the stale threshold and
   * has no active timer is a candidate for re-arm or force-completion.
   *
   * Optional for backward compatibility with in-flight games (treat missing as
   * "unknown" — the watchdog will conservatively skip games without it).
   * SERVER-ONLY: never sent to frontend clients.
   */
  lastActivityAt?: string;
}

/**
 * A single turn-log entry recording what happened in one dice-roll + action sequence.
 *
 * SERVER-ONLY: this type is stored in Redis as part of game state but is never
 * sent to frontend clients. It is used by the opponent-profiling pipeline (Epic 10).
 *
 * One entry is appended per pawn action:
 *  - 'release'  — player released a pawn from base (always on a 6)
 *  - 'move'     — player moved a pawn already on the board
 *  - 'no-move'  — player had no legal moves (or chose to pass), turn advanced without a pawn action
 */
export interface TurnLogEntry {
  /** Monotonic turn counter for this game (increments after each action). */
  turnNumber: number;
  /** Player who acted this turn. */
  playerId: string;
  /** Board colour of the acting player. */
  playerColor: PlayerColor;
  /** Dice value rolled this turn (1–6). */
  diceValue: number;
  /** What kind of pawn action was taken. */
  moveKind: 'release' | 'move' | 'no-move';
  /** Player ID of the captured opponent, if a capture occurred this turn. */
  capturedOpponentPlayerId?: string;
  /**
   * True if the player had at least one move that would have captured an opponent.
   * Drives the `capturesDeclined` counter in the opponent-profile updater (Story 10-04).
   */
  pawnHadLegalCapture: boolean;
  /** True if the action released a pawn from base (moveKind === 'release' implies this). */
  releasedFromBase: boolean;
  /**
   * True if the pawn's landing square is a known SAFE_SQUARE (start square or
   * safe star).  Drives the `defensiveMoves` counter in the opponent-profile
   * updater (Story 10-04).  False for `no-move` entries (no landing occurred).
   */
  landedOnSafeSquare: boolean;
  /** Unix ms timestamp when the action was recorded. */
  timestamp: number;
}

/**
 * Mapping of player colors to their start positions on the board
 * Start positions are where pawns enter the main board path after release from base
 */
export const COLOR_TO_START_POSITION: Record<PlayerColor, number> = {
  green: 0,
  yellow: 13,
  blue: 26,
  red: 39
} as const;

/**
 * Game economy constants for paid games
 */
export const GAME_ECONOMY = {
  /** Entry fee required to join a paid game (SOKA points) */
  PAID_ENTRY_FEE: 200
} as const;

/**
 * Free game rewards
 * Awarded only for free matchmaking games (isMatchmaking === true).
 * Private free rooms (isMatchmaking === false) receive no rewards (Story 02-01).
 */
export const FREE_GAME_REWARDS = {
  /** 1st place reward (SOKA points) */
  FIRST_PLACE: 25,
  /** 2nd place reward for 3-4 player games (SOKA points) */
  SECOND_PLACE: 5
} as const;
